import {Component} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {environment} from "../../../../themes/openems/environments/edge-dev";
import {Api} from "../../../generated/api/hydrosolid-portal/api";
import {
  siteControllerDeleteUserFromSite,
  siteControllerFindBySite,
  siteControllerUpdateUserSiteRole
} from "../../../generated/api/hydrosolid-portal/functions";
import {SiteUserWithMailDto} from "../../../generated/api/hydrosolid-portal/models/site-user-with-mail-dto";
import {Edge} from "../../../shared/components/edge/edge";
import {AuthService} from "../../../shared/service/auth.service";
import {Service} from "../../../shared/service/service";
import {Role} from "../../../shared/type/role";
import {InfiniteScrollCustomEvent, ModalController, ViewWillEnter} from "@ionic/angular";
import {ConfirmModalComponent} from "../../../shared/confirm-modal/confirm-modal.component";

type UserRoleType = "admin" | "installer" | "owner" | "guest";

interface UsersViewModel {
  id: number;
  email: string;
  role: Role;
  userUid: string;
}


@Component({
  selector: "app-users-list",
  templateUrl: "./users.component.html",
  standalone: false,
})
export class UsersComponent implements ViewWillEnter {

  public isLoading = false;

  public isAtLeastOwner = false;
  public isAtLeastInstaller = false;
  public isAtLeastAdmin = false;

  public users: UsersViewModel[] = [];

  protected readonly Role = Role;
  protected readonly environment = environment;
  protected loggedInUserUid: string | undefined;

  protected isPlatformAdmin = false;

  /** Limits edges in pagination response */
  private readonly limit: number = 20;
  /** True, if all available edges for this user had been retrieved */
  private limitReached: boolean = false;
  private page = 0;

  private edge: Edge | undefined;

  constructor(public service: Service,
              public modalCtrl: ModalController,
              protected route: ActivatedRoute,
              private authService: AuthService,
              private apiService: Api) {

    this.loggedInUserUid = authService.parsedToken?.sub;
  }

  ionViewWillEnter() {

    this.isPlatformAdmin = this.authService.userRoles.some(role => role.toLowerCase() === "admin");

    this.resetPage();
    this.init();
  }

  /**
   * Updates available users on scroll-event
   *
   * @param infiniteScroll the InfiniteScrollCustomEvent
   */
  doInfinite(infiniteScroll: InfiniteScrollCustomEvent) {
    setTimeout(() => {
      this.page++;
      this.loadNextPage().then((edges) => {
        this.users.push(...edges);
        infiniteScroll.target.complete();
      }).catch(() => {
        infiniteScroll.target.complete();
      });
    }, 200);
  }

  loadNextPage(): Promise<UsersViewModel[]> {
    this.isLoading = true;

    return new Promise<UsersViewModel[]>((resolve, reject) => {

      if (this.limitReached) {
        resolve([]);
        return;
      }

      return this.apiService.invoke(siteControllerFindBySite, {
        id: this.edge?.id ?? "",
        take: this.limit,
        page: this.page,
      }).then(userSitePageDto => {
        const users = userSitePageDto.data.map((dto: SiteUserWithMailDto) => ({
          id: dto.id,
          email: dto.email,
          userUid: dto.userUuid,
          role: Role.getRole(dto.role),
        }));

        this.limitReached = !userSitePageDto.meta.hasNextPage;

        resolve(users);
      }).catch(err => reject(err))
        .finally(() => this.isLoading = false);

    });
  }

  onRoleChange(user: UsersViewModel, newRole: number) {
    this.isLoading = true;
    this.apiService.invoke(siteControllerUpdateUserSiteRole, {
      id: user.userUid,
      siteId: this.edge?.id ?? "",
      body: {
        newRole: Role[newRole].toLowerCase() as UserRoleType,
      },
    }).then(() => {
      this.resetPage();
      return this.loadNextPage();
    }).then(users => this.users = users)
      .finally(() => this.isLoading = false);
  }

  private init() {

    this.isLoading = true;

    this.service.getCurrentEdge().then(edge => {
      this.edge = edge;
      this.isAtLeastOwner = edge.roleIsAtLeast(Role.OWNER);
      this.isAtLeastInstaller = edge.roleIsAtLeast(Role.INSTALLER);
      this.isAtLeastAdmin = edge.roleIsAtLeast(Role.ADMIN);
    }).then(() => {
      return this.loadNextPage();
    }).then(users => this.users = users)
      .finally(() => this.isLoading = false);
  }

  private async deleteUser(user: UsersViewModel) {
    const modal = await this.modalCtrl.create({
      component: ConfirmModalComponent,
    });

    await modal.present();
    const {data} = await modal.onDidDismiss();

    if (data) {
      this.isLoading = true;
      this.apiService.invoke(siteControllerDeleteUserFromSite, {
        siteId: this.edge?.id ?? "",
        id: user.userUid,
      }).then(() => {
        this.resetPage();
        return this.loadNextPage();
      }).then((users) => this.users = users)
        .finally(() => this.isLoading = false);
    }

  }

  private resetPage() {
    this.page = 1;
    this.limitReached = false;
  }
}
